import React, { FC, MouseEvent, ReactNode, useEffect, useState } from 'react';

import { formatToCurrency } from 'infrastructure/targets/web/modules/common/helpers';

import {
  StyledToggleButton as InfoCardsStyledToggleButton,
  InfoCardVariantStyledToggleButtonGroup,
  StyledButtonLabel,
  StyledButtonsGroupContainer,
  StyledButtonSubtitle,
  StyledButtonTitle,
} from './style';

export interface IButtonGroupButton {
  title: ReactNode;
  subtitle?: ReactNode;
  value: string | number;
  additionalValue?: string | number;
  isDisabled?: boolean;
  secondaryValue?: unknown;
  tertiaryValue?: unknown;
  availableForSmallEvents?: boolean;
  price?: number;
  highlight?: boolean;
  label?: string;
}

export interface IButtonsGroup {
  buttonList?: Array<IButtonGroupButton>;
  onChange?: (value: string, event: MouseEvent<HTMLElement>) => void;
  shouldStayInOneRow?: boolean;
  buttonSections?: Array<ButtonSection>;
  selectedValue?: string | number;
  nonClickable?: boolean;
  disabledItems?: number[];
  hideDisabled?: boolean;
  infoCardVariant?: boolean;
}

export type ButtonSection = {
  sectionTitle: string;
  sectionValue: string;
  buttonList: Array<IButtonGroupButton>;
};

const desiredOrder = [
  'Swing Evaluation, 1 Hour',
  'Lesson, 0.5 Hour',
  'Lesson, 1 Hour',
  'Group Lesson, 1.5 Hour',
];

const LessonTypesButtonsGroup: FC<IButtonsGroup> = ({
  buttonList,
  onChange,
  shouldStayInOneRow,
  selectedValue,
  nonClickable,
  disabledItems,
}) => {
  const [selectedButtonValue, setSelectedButtonValue] = useState<string | number>('');
  const [sortedButtonList, setSortedButtonList] = useState<Array<IButtonGroupButton>>([]);

  const handleChange = (newValue: string, event: MouseEvent<HTMLElement>) => {
    if (onChange) {
      onChange(newValue, event);
    }
    setSelectedButtonValue(newValue);
  };

  useEffect(() => {
    if (selectedValue === 'unset' || selectedValue === undefined) {
      setSelectedButtonValue('');
    }
    if (selectedValue) {
      setSelectedButtonValue(selectedValue);
    }
  }, [selectedValue]);

  useEffect(() => {
    if (buttonList && buttonList.length > 0) {
      // Sort the buttonList based on the desired order
      const sortedButtonList = [...buttonList].sort((a, b) => {
        const indexA = desiredOrder.indexOf(a.title as string);
        const indexB = desiredOrder.indexOf(b.title as string);
        return indexA - indexB;
      });

      setSortedButtonList(sortedButtonList);
    }
  }, [buttonList]);

  return (
    <StyledButtonsGroupContainer>
      <InfoCardVariantStyledToggleButtonGroup
        color="secondary"
        value={selectedButtonValue}
        numberofbuttons={sortedButtonList?.length || 0}
        shouldstayinonerow={shouldStayInOneRow ? 1 : 0}
        exclusive
        onChange={(event, val) => (nonClickable ? null : handleChange(val, event))}>
        {sortedButtonList?.map(
          (
            {
              title,
              value,
              isDisabled,
              subtitle,
              additionalValue,
              secondaryValue,
              tertiaryValue,
              price,
              label,
            },
            idx,
          ) => (
            <InfoCardsStyledToggleButton
              data-testid={`buttonGroupButton-${value}`}
              key={`${idx}${value}`}
              value={value}
              data-additionalvalue={`${additionalValue}`}
              data-secondaryvalue={`${secondaryValue}`}
              data-tertiaryvalue={`${tertiaryValue}`}
              disableRipple={nonClickable}
              disableFocusRipple={nonClickable}
              sx={{
                minWidth: shouldStayInOneRow ? '100px' : '115px',
                ...(shouldStayInOneRow && { maxWidth: '170px' }),
                '&:hover': {
                  backgroundColor: nonClickable ? 'transparent' : '',
                },
                cursor: nonClickable ? 'default' : 'pointer',
              }}
              disabled={isDisabled || disabledItems?.includes(value as number)}>
              {typeof title === 'string' ? (
                // format on two lines with title and length on next line
                (() => {
                  const [main, length] = title.split(',');
                  return (
                    <StyledButtonTitle variant="body2">
                      {main}
                      <br />
                      {length?.trim()}
                    </StyledButtonTitle>
                  );
                })()
              ) : (
                // could be JSX just render it as-is
                <StyledButtonTitle variant="body2">{title}</StyledButtonTitle>
              )}
              {subtitle && <StyledButtonSubtitle variant="h6">{subtitle}</StyledButtonSubtitle>}
              {!isDisabled && (price ?? -1) > -1 && (
                <StyledButtonSubtitle variant="h6">
                  {`${formatToCurrency(price)}`}
                </StyledButtonSubtitle>
              )}
              {label && <StyledButtonLabel variant="body2">{label}</StyledButtonLabel>}
            </InfoCardsStyledToggleButton>
          ),
        )}
      </InfoCardVariantStyledToggleButtonGroup>
    </StyledButtonsGroupContainer>
  );
};

export default LessonTypesButtonsGroup;
